import React, { useEffect, useRef, useState } from 'react'
import { connect } from 'react-redux'
import { Row, Button, Tooltip } from 'antd'
import { CaretLeftOutlined, CaretRightOutlined } from '@ant-design/icons'
import * as d3 from 'd3'
import moment from 'moment'
import '../../../css/patient.scss'
import { CHART_COLORS } from '../../../util/chartPalette'
import { DataViewToggle, ChartDataGrid } from '../../graph/chartDataView'
import {
  quarterHourTemperature, quarterRangeLabel, earliestDay, isTodayOrLater, compactHourLabel,
  isWorn, isPlausible, wornPercent, WORN_MIN_F,
} from './tracpatch.hourly'
import { styleAxisTicks, AXIS_TITLE_FONT_SIZE, AXIS_TITLE_FONT_WEIGHT, ShowValuesSwitch } from './tracpatch.chart.style'

const cToF = (c) => (c == null ? null : c * 9 / 5 + 32)
const round1 = (v) => (v == null ? null : Math.round(v * 10) / 10)

// Ranges: 1 Day (one local day, hourly averages, navigable a day at a time up
// to today) or 7 / 14 / 30 days (daily averages). Defaults to 1 Day.
const TracPatchTemperatureChart = (props) => {
  const svgRef = useRef(null)
  const containerRef = useRef(null)
  const tooltipRef = useRef(null)
  // Defaults to 30 days like Steps and Cadence: the charts are read for trend
  // first, and the 1-Day view is now reachable by clicking a point.
  const [days, setDays] = useState(30)
  const [day, setDay] = useState(() => moment().startOf('day'))
  const [unit, setUnit] = useState('F') // 'F' default; 'C' toggle
  const [view, setView] = useState('chart')

  const [showValues, setShowValues] = useState(false)

  const hourly = days === 1


  // Low-battery alerts falling inside the window the chart is showing.
  //
  // WHY THEY LIVE ON THIS CHART. A flat battery is the usual reason the
  // temperature line has gaps, so the explanation belongs beside the symptom:
  // a coach wondering "why is there no data on the 14th" sees the marker on
  // the 14th. Clustered markers are the signal to call the patient and teach
  // them to charge nightly -- many new patients do not know they must.
  //
  // 🔴 A MARKER MEANS "AN ALERT WAS SENT", NOT "THE BATTERY WAS LOW", and the
  // absence of one proves nothing. A row is written only when the push
  // succeeded, at most once per calendar day per tier, and the phone reads the
  // battery only while BLE-connected. A device that died unattended overnight
  // leaves no marker at all. The tooltip says "alert sent" for that reason.
  const alertsInWindow = (start, end) => {
    const list = props.batteryAlerts || []
    const out = []
    for (let i = 0; i < list.length; i++) {
      const a = list[i]
      if (!a || !a.sentAt) continue
      const t = new Date(a.sentAt)
      if (isNaN(t.getTime())) continue
      if (t >= start && t <= end) out.push({ at: t, percent: a.percent })
    }
    return out.sort((p, q) => p.at - q.at)
  }

  // The alerts belonging to a DRAWN window. Both the markers and the header
  // count go through this, so the two cannot describe different spans.
  //
  // The header used to compute its own today-anchored window
  // (`moment().subtract(days, 'days')` → `moment()`). That was equivalent until
  // the empty-window fallback landed: for a patient whose last reading predates
  // the range, getDailyTemp slides the window back to END on that reading, and
  // the two then disagreed in both directions — markers drawn with no count
  // beside them, or a count naming alerts that appear nowhere on the chart.
  //
  // The daily series ends at 00:00 of the last day holding data, so the end is
  // extended to that day's close. Without it an alert sent later the same day
  // is dropped — including today's, on the ordinary non-stale path. The hourly
  // window already ends at the following midnight and is used as-is.
  const alertsForWindow = (ws, we) => {
    if (!ws || !we) return []
    return alertsInWindow(ws, hourly ? we : moment(we).endOf('day').toDate())
  }


  useEffect(() => {
    if (view === 'chart') draw()
  }, [props.data, days, unit, view, day.valueOf(), showValues]) // eslint-disable-line react-hooks/exhaustive-deps

  const toUnit = (c) => round1(unit === 'F' ? cToF(c) : c)

  var getDailyTemp = () => {
    if (!props.data || props.data.length === 0) return { dailyData: [] }

    // The window normally ends today and reaches back `days`. When the patient
    // has NO readings in that window it ANCHORS TO THEIR LATEST READING
    // instead, showing the most recent `days` that actually contain data.
    //
    // 🔴 WHY, because the old behaviour looked like data loss. Records outside
    // the window are dropped below, so a patient whose last reading was four
    // months ago rendered an empty chart -- while the patient list beside it
    // showed their latest temperature, pain score, flexion and steps, because
    // fetchTracPatchSummary takes the latest record with no date cutoff. The
    // page contradicted itself and the clinician reasonably concluded the data
    // had been lost. Reported from staging 2026-11-03.
    //
    // Widening the default range would not have fixed it -- it moves the cliff
    // rather than removing it. A patient inactive for longer than whatever
    // window you pick still shows nothing.
    //
    // `isStale` is returned so the chart can SAY it is showing an older span.
    // Silently relabelling the axis would trade one misleading screen for
    // another: a clinician must never read four-month-old readings as current.
    const today = moment().startOf('day')
    let rangeEnd = today
    let isStale = false

    let latest = null
    props.data.forEach((record) => {
      const m = moment(record.recorded_at)
      if (m.isValid() && (latest === null || m.isAfter(latest))) latest = m
    })

    let defaultStart = moment().subtract(days - 1, 'days').startOf('day')
    if (latest !== null && latest.isBefore(defaultStart)) {
      rangeEnd = latest.clone().startOf('day')
      defaultStart = rangeEnd.clone().subtract(days - 1, 'days')
      isStale = true
    }

    // Group by day, accumulate sum + count for averaging (only count when a value exists)
    const byDay = {}
    props.data.forEach((record) => {
      const day = moment(record.recorded_at).format('YYYY-MM-DD')
      if (moment(record.recorded_at).isBefore(defaultStart)) return
      if (!byDay[day])
        byDay[day] = {
          femurSum: 0, femurCount: 0, tibiaSum: 0, tibiaCount: 0,
          offFemurSum: 0, offFemurCount: 0, offTibiaSum: 0, offTibiaCount: 0,
          lastTime: null,
        }
      // Track the day's latest reading timestamp for the data-grid's date+time.
      if (!byDay[day].lastTime || moment(record.recorded_at).isAfter(moment(byDay[day].lastTime))) {
        byDay[day].lastTime = record.recorded_at
      }
      // Same split as the 15-minute view: a day's average must describe the
      // time the device was ON THE LEG, or a patch left on a nightstand all
      // afternoon drags the day toward room temperature.
      // isPlausible, not `!= null`: a reading above TEMP_MAX_F is sensor
      // garbage rather than an off-leg observation, and the off-leg series is
      // inside the Y domain (see below), so keeping it would stretch the axis.
      if (isPlausible(record.femur_temp_c)) {
        if (isWorn(record.femur_temp_c)) {
          byDay[day].femurSum += record.femur_temp_c
          byDay[day].femurCount += 1
        } else {
          byDay[day].offFemurSum += record.femur_temp_c
          byDay[day].offFemurCount += 1
        }
      }
      if (isPlausible(record.tibia_temp_c)) {
        if (isWorn(record.tibia_temp_c)) {
          byDay[day].tibiaSum += record.tibia_temp_c
          byDay[day].tibiaCount += 1
        } else {
          byDay[day].offTibiaSum += record.tibia_temp_c
          byDay[day].offTibiaCount += 1
        }
      }
    })

    const dataDays = Object.keys(byDay).sort()
    if (dataDays.length === 0) return { dailyData: [] }

    // Left-justify to first data day; right-justify to last data day so the
    // chart has no blank tail past the most recent reading.
    const firstData = moment(dataDays[0], 'YYYY-MM-DD').startOf('day')
    const lastData = moment(dataDays[dataDays.length - 1], 'YYYY-MM-DD').startOf('day')
    const windowStart = firstData.isAfter(defaultStart) ? firstData : defaultStart
    const windowEnd = lastData.isBefore(rangeEnd) ? lastData : rangeEnd

    // Emit one entry per day in the window; missing days get nulls so the line breaks
    const dailyData = []
    const cursor = windowStart.clone()
    while (cursor.isSameOrBefore(windowEnd)) {
      const key = cursor.format('YYYY-MM-DD')
      const d = byDay[key]
      dailyData.push({
        date: cursor.clone().toDate(),
        time: d ? d.lastTime : null,
        femur: toUnit(d && d.femurCount > 0 ? d.femurSum / d.femurCount : null),
        tibia: toUnit(d && d.tibiaCount > 0 ? d.tibiaSum / d.tibiaCount : null),
        offFemur: toUnit(d && d.offFemurCount > 0 ? d.offFemurSum / d.offFemurCount : null),
        offTibia: toUnit(d && d.offTibiaCount > 0 ? d.offTibiaSum / d.offTibiaCount : null),
        wornCount: d ? Math.max(d.femurCount, d.tibiaCount) : 0,
        offCount: d ? Math.max(d.offFemurCount, d.offTibiaCount) : 0,
      })
      cursor.add(1, 'day')
    }
    return {
      dailyData,
      windowStart: windowStart.toDate(),
      windowEnd: windowEnd.toDate(),
      isStale,
    }
  }

  // 1 Day: 96 FIFTEEN-MINUTE averages for the selected day. The x window is
  // always the full day (midnight to midnight) so the time of day reads off
  // the axis.
  //
  // 🔴 Fifteen minutes, not an hour, because the question this view answers is
  // "was the device actually being worn?". Measured on a test patient
  // 2026-09-28: off-leg readings sat at 77-78 °F, on-leg at 88-92 °F, and the
  // changeover took under fifteen minutes. Averaged across an hour that
  // transition collapses into one lukewarm number that is true of neither
  // state -- and a device taken off for half an hour disappears entirely.
  var getHourlyTemp = () => {
    const rows = quarterHourTemperature(props.data, day).map((h) => ({
      slot: h.slot,
      hour: h.hour,
      date: h.date,
      time: h.time,
      femur: toUnit(h.femurC),
      tibia: toUnit(h.tibiaC),
      offFemur: toUnit(h.offFemurC),
      offTibia: toUnit(h.offTibiaC),
      wornCount: h.wornCount,
      offCount: h.offCount,
    }))
    const hasData = rows.some((r) => r.femur != null || r.tibia != null || r.offFemur != null || r.offTibia != null)
    return {
      dailyData: hasData ? rows : [],
      windowStart: day.clone().startOf('day').toDate(),
      windowEnd: day.clone().startOf('day').add(1, 'day').toDate(),
    }
  }

  var getSeries = () => (hourly ? getHourlyTemp() : getDailyTemp())

  var draw = () => {
    const { dailyData, windowStart, windowEnd } = getSeries()
    if (!dailyData || dailyData.length === 0) return

    const margin = { top: 20, right: 20, bottom: 60, left: 50 }
    const screenWidth = containerRef.current ? containerRef.current.offsetWidth : 400
    const screenHeight = 300
    const width = screenWidth - margin.left - margin.right
    const height = screenHeight - margin.top - margin.bottom

    const svg = d3.select(svgRef.current)
    svg.selectAll('*').remove()
    // A redraw replaces the point under the pointer, so its mouseout never
    // fires and a tooltip left visible would float there until the next
    // hover. Every draw therefore starts with the tooltip hidden.
    d3.select(tooltipRef.current).style('visibility', 'hidden')

    const g = svg
      .attr('width', screenWidth)
      .attr('height', screenHeight)
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`)

    const x = d3.scaleTime().domain([windowStart, windowEnd]).range([0, width])

    // Off-leg values are in the domain too, or the grey points fall off the
    // bottom of the chart and the thing we kept them to show is invisible.
    const allValues = dailyData
      .flatMap((d) => [d.femur, d.tibia, d.offFemur, d.offTibia])
      .filter((v) => v != null)
    const yMin = Math.max(0, Math.min(...allValues) - 1)
    const yMax = Math.max(...allValues) + 1
    const y = d3.scaleLinear().domain([yMin, yMax]).range([height, 0])

    // X axis: hours of the day (every 3 h) in 1-Day mode, dates otherwise —
    // one tick per day while the window is short, so a 7-day view of three
    // days of data does not repeat each date at 12-hour ticks.
    const spanDays = Math.round((windowEnd - windowStart) / 86400000) + 1
    const xAxis = hourly
      ? d3.axisBottom(x).ticks(d3.timeHour.every(2)).tickFormat((t) => compactHourLabel(moment(t).hour()))
      : d3.axisBottom(x).ticks(spanDays <= 14 ? d3.timeDay.every(1) : 8).tickFormat(d3.timeFormat('%m/%d'))
    g.append('g')
      .attr('transform', `translate(0,${height})`)
      .call(xAxis)
      .selectAll('text')
      .style('text-anchor', 'end')
      .attr('dx', '-.8em')
      .attr('dy', '.15em')
      .attr('transform', 'rotate(-45)')
      .call(styleAxisTicks, days > 20 ? 10 : 11)
      .style('fill', 'black')
      .style('stroke', 'none')

    // X label
    g.append('text')
      .attr('text-anchor', 'end')
      .attr('x', width / 2 + margin.left)
      .attr('y', height + margin.bottom - 5)
      .attr('stroke', 'none')
      .attr('fill', 'black')
      .style('font-size', AXIS_TITLE_FONT_SIZE)
      .style('font-weight', AXIS_TITLE_FONT_WEIGHT)
      .text(hourly ? 'Time' : 'Date')

    // Y axis
    g.append('g')
      .call(d3.axisLeft(y).ticks(6))
      .selectAll('text')
      .style('fill', 'black')
      .style('stroke', 'none')
      .call(styleAxisTicks, 10)

    // Y label
    g.append('text')
      .attr('transform', 'rotate(-90)')
      .attr('y', -margin.left + 12)
      .attr('x', -height / 2)
      .attr('text-anchor', 'middle')
      .attr('fill', 'black')
      .attr('stroke', 'none')
      .style('font-size', AXIS_TITLE_FONT_SIZE)
      .style('font-weight', AXIS_TITLE_FONT_WEIGHT)
      .text('Avg Temperature (°' + unit + ')')

    const tooltip = d3.select(tooltipRef.current)
    const whenLabel = (d) =>
      hourly
        ? `<b>Time</b>: ${quarterRangeLabel(d.slot)}<br/><b>Date</b>: ${moment(d.date).format('MM/DD')}`
        : `<b>Date</b>: ${moment(d.date).format('MM/DD')}`

    // ── Low-battery markers ────────────────────────────────────────────────
    // Drawn BEFORE the temperature lines so a marker can never sit on top of
    // a data point and intercept its hover. Anchored to the x axis rather
    // than to a y value, because an alert has no temperature -- it is an
    // event on the timeline, not a reading.
    const batteryAlerts = alertsForWindow(windowStart, windowEnd)
    if (batteryAlerts.length > 0) {
      const markers = g
        .append('g')
        .attr('class', 'battery-alert-markers')
        .selectAll('.battery-alert')
        .data(batteryAlerts)
        .enter()
        .append('g')
        .attr('class', 'battery-alert')
        // scaleTime does not clamp, and the window end above is extended to the
        // close of the last day — so an alert later that day would land past the
        // right edge. Pin it to the final tick, which is the day it happened on.
        .attr('transform', (d) => `translate(${Math.min(x(d.at), width)},${height})`)
        .style('cursor', 'default')

      // A short stem up from the axis, so the marker reads as "at this
      // moment" rather than floating at an arbitrary temperature.
      markers
        .append('line')
        .attr('y1', 0)
        .attr('y2', -14)
        .attr('stroke', '#d4380d')
        .attr('stroke-width', 1.5)

      markers
        .append('circle')
        .attr('cy', -18)
        .attr('r', 5)
        .attr('fill', '#d4380d')

      // A battery glyph would not survive at 10px; "!" is legible and the
      // tooltip carries the meaning.
      markers
        .append('text')
        .attr('y', -15)
        .attr('text-anchor', 'middle')
        .attr('fill', '#fff')
        .style('font-size', '9px')
        .style('font-weight', '700')
        .style('pointer-events', 'none')
        .text('!')

      markers
        .on('mouseover', (d) => {
          tooltip
            .style('visibility', 'visible')
            .html(
              `<b>Low-battery alert sent</b><br/>` +
                `<b>When</b>: ${moment(d.at).format('MM/DD h:mm A')}<br/>` +
                `<b>Tier</b>: at or below ${d.percent}%` +
                `<br/><span style="opacity:.75">The patient was notified to charge the device.</span>`
            )
        })
        .on('mousemove', () => {
          tooltip
            .style('top', d3.event.pageY - 10 + 'px')
            .style('left', d3.event.pageX + 12 + 'px')
        })
        .on('mouseout', () => tooltip.style('visibility', 'hidden'))
    }

    const drawLine = (key, label, color) => {
      // Pre-filter to defined points so the line connects across missing
      // days instead of breaking. The dots below still render only on
      // days with data so the underlying gaps remain visible if you look.
      const lineData = dailyData.filter((d) => d[key] != null)
      const lineGen = d3
        .line()
        .x((d) => x(d.date))
        .y((d) => y(d[key]))
        .curve(d3.curveMonotoneX)

      g.append('path')
        .datum(lineData)
        .attr('fill', 'none')
        .attr('stroke', color)
        .attr('stroke-width', 2)
        .attr('d', lineGen)

      g.selectAll(`.dot-${key}`)
        .data(dailyData.filter((d) => d[key] != null))
        .enter()
        .append('circle')
        .attr('class', `dot-${key}`)
        .attr('cx', (d) => x(d.date))
        .attr('cy', (d) => y(d[key]))
        .attr('r', 4)
        .attr('fill', color)
        .style('cursor', hourly ? 'pointer' : 'zoom-in')
        .on('click', (d) => {
          if (hourly || !d.date) return
          tooltip.style('visibility', 'hidden')
          setDay(moment(d.date).startOf('day'))
          setDays(1)
        })
        .on('mouseover', function (d) {
          tooltip
            .style('visibility', 'visible')
            .html(
              `<b>${label}</b>: ${d[key]}°${unit}<br/>${whenLabel(d)}` +
                (hourly ? '' : '<br/><i>Click for this day</i>')
            )
            .style('top', d3.select(this).attr('cy') - 50 + 'px')
            .style('left', parseInt(d3.select(this).attr('cx')) + margin.left + 10 + 'px')
        })
        .on('mouseout', () => tooltip.style('visibility', 'hidden'))
    }

    drawLine('femur', 'Femur Avg', CHART_COLORS.lineBlue)
    drawLine('tibia', 'Tibia Avg', CHART_COLORS.secondaryRed)

    // Off-leg readings: plotted GREY, as points only, never joined by a line.
    //
    // They are excluded from every average above -- that is the whole point of
    // the threshold -- but deleting them outright would make "the patient did
    // not wear it" indistinguishable from "the device sent nothing", and those
    // two call for opposite responses. Grey says: we heard from the device, it
    // was not on the leg.
    const drawOffLeg = (key, label) => {
      g.selectAll(`.off-${key}`)
        .data(dailyData.filter((d) => d[key] != null))
        .enter()
        .append('circle')
        .attr('class', `off-${key}`)
        .attr('cx', (d) => x(d.date))
        .attr('cy', (d) => y(d[key]))
        .attr('r', 3)
        .attr('fill', '#b0b6bd')
        .style('cursor', hourly ? 'pointer' : 'zoom-in')
        .on('mouseover', function (d) {
          tooltip
            .style('visibility', 'visible')
            .html(`<b>${label}</b>: ${d[key]}°${unit}<br/><i>device off the leg</i><br/>${whenLabel(d)}`)
            .style('top', d3.select(this).attr('cy') - 50 + 'px')
            .style('left', parseInt(d3.select(this).attr('cx')) + margin.left + 10 + 'px')
        })
        .on('mouseout', () => tooltip.style('visibility', 'hidden'))
    }
    drawOffLeg('offFemur', 'Femur')
    drawOffLeg('offTibia', 'Tibia')

    // Value labels — Femur only (Tibia kept on the chart but unlabeled),
    // every Nth point from the latest going back: every point up to 14 days,
    // every other at 30 days or when more than 12 hours of a day have data.
    // 3px above the femur dot.
    // OFF unless the chart's "Show values" switch is on.
    const labelData = showValues ? dailyData.filter((d) => d.femur != null) : []
    // Thin by how many labels there ACTUALLY are, not by the view's slot count.
    // A fixed "every 8th" was wrong: the 1-Day view has 96 slots but labelData
    // only holds the ones with readings, so a day with four readings printed
    // one label. Cap at roughly a dozen and the rule works for a sparse day and
    // a fully-populated one alike.
    const MAX_LABELS = 12
    const labelStep = Math.max(1, Math.ceil(labelData.length / MAX_LABELS))
    labelData.forEach((d, i) => {
      const fromEnd = labelData.length - 1 - i
      if (fromEnd % labelStep !== 0) return
      g.append('text')
        .attr('x', x(d.date))
        .attr('y', y(d.femur) - 3)
        .attr('text-anchor', 'middle')
        .attr('fill', CHART_COLORS.lineBlue)
        .style('font-size', '10px')
        .style('font-weight', '600')
        .style('pointer-events', 'none')
        .text(d.femur + '°' + unit)
    })

    // Legend
    const legend = [
      { label: 'Femur', color: CHART_COLORS.lineBlue },
      { label: 'Tibia', color: CHART_COLORS.secondaryRed },
    ]
    let xOffset = 0
    legend.forEach((item) => {
      g.append('rect').attr('x', xOffset).attr('y', 0).attr('width', 10).attr('height', 10).attr('fill', item.color)
      g.append('text').attr('x', xOffset + 14).attr('y', 9).attr('fill', 'black').attr('stroke', 'none').style('font-size', '12px').text(item.label)
      xOffset += 65
    })
  }

  // Also drives the stale-window banner below. getSeries is cheap (it reduces
  // an in-memory array) and is already called twice; a third call keeps the
  // banner and the drawn chart reading the SAME computation rather than two
  // that could drift.
  const { dailyData, isStale, windowStart: staleStart, windowEnd: staleEnd } = getSeries()
  // Counted over the window just drawn. No window means no readings at all
  // (getDailyTemp returns early), so fall back to the plain range — a patient
  // can have battery alerts and no temperature data, and that count is still
  // true and still worth showing.
  const headerAlerts =
    staleStart && staleEnd
      ? alertsForWindow(staleStart, staleEnd)
      : alertsInWindow(
          (hourly ? day.clone().startOf('day') : moment().subtract(days, 'days').startOf('day')).toDate(),
          (hourly ? day.clone().endOf('day') : moment()).toDate()
        )
  // The full sentence lives in the TOOLTIP, not the controls row: spelled out
  // inline it pushed the Data View button past the right edge of the card at
  // half width.
  //
  // It names the SPAN DRAWN, with dates, rather than the range button. Two
  // things make "in 30 days" false even though the 30 Days button is pressed:
  // a stale window ends on the patient's last reading, and even a current one
  // is left-justified to their first. A patient three days into treatment is
  // shown three days. Since the count is now taken over exactly what is drawn,
  // the label has to say what that was.
  const fmtAlertDay = (d) => moment(d).format('MMM D')
  const headerAlertSummary =
    headerAlerts.length +
    ' low-battery ' +
    (headerAlerts.length === 1 ? 'alert' : 'alerts') +
    (hourly
      ? ' on ' + day.format('MMM D')
      : staleStart && staleEnd
        ? ' between ' + fmtAlertDay(staleStart) + ' and ' + fmtAlertDay(staleEnd)
        : ' in ' + days + ' days')
  // Share of this range's readings taken on the leg. Null when the device
  // reported nothing at all -- 0% would claim it was never worn, which is a
  // different statement from never having heard from it.
  const worn = wornPercent(dailyData)
  const gridRows = dailyData
    .filter((d) => d.femur != null || d.tibia != null)
    .map((d) => ({
      date: d.time || d.date,
      femur: d.femur == null ? '—' : d.femur + '°' + unit,
      tibia: d.tibia == null ? '—' : d.tibia + '°' + unit,
    }))
  const gridColumns = [
    { title: 'Femur (°' + unit + ')', dataIndex: 'femur', key: 'femur' },
    { title: 'Tibia (°' + unit + ')', dataIndex: 'tibia', key: 'tibia' },
  ]

  // Day navigation (1 Day only): back one day at a time as far as the first
  // reading, forward one day at a time up to today.
  const firstDay = earliestDay(props.data, 'recorded_at')
  const canGoBack = !!firstDay && day.isAfter(firstDay)
  const canGoForward = !isTodayOrLater(day)
  const shiftDay = (n) => setDay(day.clone().add(n, 'day').startOf('day'))

  return (
    <>
      <center>
        <h4>TracPatch Temperature</h4>
        {worn != null && (
          <div style={{ fontSize: 12, color: '#627184', marginTop: -6, marginBottom: 6 }}>
            Device worn <b>{worn}%</b> of readings in this range
            <span style={{ color: '#8a96a3' }}> (≥ {WORN_MIN_F}°F)</span>
          </div>
        )}
      </center>
      {/* Says plainly that the span on screen is not the span requested. The
          chart falls back to the most recent days that hold data, and a
          clinician must never read four-month-old temperatures as current. */}
      {!hourly && isStale && staleStart && staleEnd && (
        <Row justify='start' style={{ marginBottom: 8, paddingLeft: 12 }}>
          <div
            style={{
              background: '#fff8e1',
              border: '1px solid var(--orange-color, #f5881f)',
              borderRadius: 8,
              padding: '6px 12px',
              fontSize: 12,
              color: '#8a4b00',
              maxWidth: '100%',
            }}
          >
            <strong>No readings in the last {days} days.</strong>{' '}
            Showing {moment(staleStart).format('MMM D, YYYY')} –{' '}
            {moment(staleEnd).format('MMM D, YYYY')}, the most recent data
            available for this patient.
          </div>
        </Row>
      )}
      <Row justify='start' className='chartControls' style={{ marginBottom: 8, paddingLeft: 12 }}>
        <Button.Group size='small'>
          <Button type={days === 1 ? 'primary' : 'default'} onClick={() => setDays(1)}>1 Day</Button>
          <Button type={days === 7 ? 'primary' : 'default'} onClick={() => setDays(7)}>7 Days</Button>
          <Button type={days === 14 ? 'primary' : 'default'} onClick={() => setDays(14)}>14 Days</Button>
          <Button type={days === 30 ? 'primary' : 'default'} onClick={() => setDays(30)}>30 Days</Button>
        </Button.Group>
        <Button.Group size='small'>
          <Button type={unit === 'F' ? 'primary' : 'default'} onClick={() => setUnit('F')}>°F</Button>
          <Button type={unit === 'C' ? 'primary' : 'default'} onClick={() => setUnit('C')}>°C</Button>
        </Button.Group>
        <ShowValuesSwitch id='temp-show-values' checked={showValues} onChange={setShowValues} />
        {/* Count for the range currently shown, so "3 in 30 days" and "1 in
            7 days" cannot look like contradictory numbers. Hidden at zero --
            no alerts is the normal state and a permanent "0" would be noise
            that teaches people to ignore the row. */}
        {headerAlerts.length > 0 && (
          <Tooltip
            title={
              <div>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>
                  {headerAlertSummary}
                </div>
                <div style={{ marginBottom: 4 }}>
                  Alerts sent to the patient to charge the device:
                </div>
                {headerAlerts.map((a) => (
                  <div key={a.at.getTime()}>
                    {moment(a.at).format('ddd MMM D, h:mm A')} — at or below {a.percent}%
                  </div>
                ))}
                <div style={{ marginTop: 6, opacity: 0.75 }}>
                  Not a battery history: an alert is only recorded when the
                  notification reached the phone.
                </div>
              </div>
            }
          >
            <span
              style={{
                marginLeft: 12,
                color: '#d4380d',
                fontWeight: 600,
                fontSize: 12,
                cursor: 'help',
                whiteSpace: 'nowrap',
              }}
              aria-label={headerAlertSummary}
            >
              ⚡ {headerAlerts.length}
            </span>
          </Tooltip>
        )}
        <DataViewToggle
          active={view === 'data'}
          onClick={() => setView(view === 'data' ? 'chart' : 'data')}
        />
      </Row>
      {hourly && (
        <Row justify='center' align='middle' className='chartDayNav' style={{ marginBottom: 8, gap: 8 }}>
          <Button
            size='small'
            aria-label='Previous day'
            icon={<CaretLeftOutlined />}
            disabled={!canGoBack}
            onClick={() => shiftDay(-1)}
          />
          <span style={{ minWidth: 150, textAlign: 'center', fontWeight: 600 }}>{day.format('ddd, MMM D, YYYY')}</span>
          <Button
            size='small'
            aria-label='Next day'
            icon={<CaretRightOutlined />}
            disabled={!canGoForward}
            onClick={() => shiftDay(1)}
          />
        </Row>
      )}
      {dailyData.length > 0 ? (
        view === 'data' ? (
          <ChartDataGrid rows={gridRows} valueColumns={gridColumns} csvName='tracpatch-temperature' />
        ) : (
          <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
            <div ref={tooltipRef} className='chartToolTip' style={{ position: 'absolute', visibility: 'hidden', zIndex: 10 }} />
            <svg style={{ width: '100%', height: '300px' }} ref={svgRef} />
          </div>
        )
      ) : (
        <Row justify='center' align='middle' style={{ height: 200 }}>
          <h2>{hourly ? 'No readings for this day' : 'Data not available'}</h2>
        </Row>
      )}
    </>
  )
}

const mapStateToProps = (state) => ({ patient: state.patient.patient })
const mapDispatchToProps = () => ({})
export default connect(mapStateToProps, mapDispatchToProps)(TracPatchTemperatureChart)
